import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { type AdminRole } from '@avanta/shared/types/enterprises';
import { isLoadingNewData } from '@avanta/shared/utils/table';
import { useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type Table,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useFetchEnterpriseAdminsCount } from 'hooks/useFetchEnterpriseAdminsCount';
import { EnterprisesService } from 'services/enterprises';
import { useEnterpriseId } from 'stores/user';
import { type AdminEnterprise, type GetAdminsByEnterpriseParams } from 'types/enterprises';
import { getServerSort } from 'utils/table';

const COLUMN_SORT_BY: Record<string, GetAdminsByEnterpriseParams['sortBy']> = {
  name: 'name',
  surname: 'surname',
};

const getRoleFilter = (columnFilters: ColumnFiltersState): AdminRole | undefined => {
  const roleFilterValue = columnFilters.find(filter => filter.id === 'role')?.value;

  return typeof roleFilterValue === 'string' ? roleFilterValue : undefined;
};

export const useFetchAdmins = (
  columns: ColumnDef<AdminEnterprise>[],
  pageSize: number = 10
): {
  globalFilter: string;
  table: Table<AdminEnterprise>;
  totalCount: number;
  setGlobalFilter: (value: string) => void;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const enterpriseId = useEnterpriseId();
  const debouncedFilter = useDebounce(globalFilter, 500);
  const role = getRoleFilter(columnFilters);

  const countQuery = useFetchEnterpriseAdminsCount(role);
  const totalCount = countQuery.data ?? 0;
  const params: GetAdminsByEnterpriseParams = {
    offset: pagination.pageIndex,
    limit: pagination.pageSize,
    search: debouncedFilter,
    role,
    ...getServerSort(sorting, COLUMN_SORT_BY),
  };

  const query = useQuery({
    queryKey: [QUERY_KEYS.FETCH_ADMINS, enterpriseId, params],
    queryFn: () => EnterprisesService.getAdminsByEnterprise(enterpriseId, params),
    select: response => response.data,
    placeholderData: previousData => previousData,
    enabled: !!enterpriseId,
  });

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [debouncedFilter, role]);

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    setSorting(updater);
    setPagination(previous =>
      previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }
    );
  };

  const table = useServerPaginatedTable<AdminEnterprise>({
    columns,
    data: query.data ?? [],
    totalCount,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
    columnFilters,
    onColumnFiltersChange: setColumnFilters,
    sorting,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    isLoadingData: isLoadingNewData(query) || isLoadingNewData(countQuery),
    pageCountKey: { role },
  });

  return {
    globalFilter,
    table,
    totalCount,
    setGlobalFilter,
  };
};
